import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { uploadProfileAssetToStorage } from "@/integrations/supabase/storage";
import { createListing, updateListing } from "@/lib/actions/listingActions";
import { useUserCredits } from "@/hooks/useUserCredits"; 
import { supabase } from "@/integrations/supabase/client";
import { sendListingCreationEmail } from "@/lib/actions/emailActions";

interface UseListingSubmitProps {
  userId: string;
  validateForm: () => boolean;
  formData: {
    title: string;
    description: string;
    price: string;
    category: string;
    subcategory: string;
    condition: string;
    country: string;
    location: string;
    deliveryOption: string;
    shippingCost: string;
    stock: string;
    phone: string;
    phoneCountryCode: string;
    whatsappEnabled: boolean;
    viberEnabled: boolean;
    isPriceNegotiable: string;
    email: string;
  };
  images: File[];
  videoFile: File | null;
  editMode?: boolean;
  listingId?: number;
  initialVideoUrl?: string | null;
  hasNoCreditsLeft?: boolean;
  stockItemsOverLimit?: boolean;
  stockItemsLimit?: number;
  isAuthenticated?: boolean;
}

export const useListingSubmit = ({
  userId,
  validateForm,
  formData,
  images,
  videoFile,
  editMode = false,
  listingId,
  initialVideoUrl,
  hasNoCreditsLeft = false,
  stockItemsOverLimit = false,
  stockItemsLimit = 5,
  isAuthenticated = true,
}: UseListingSubmitProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { isLoading: isLoadingCredits } = useUserCredits(
    isAuthenticated ? userId : "guest"
  );
  const [processingImages, setProcessingImages] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const uploadAssets = async (): Promise<{ imageUrls: string[], videoUrl: string | null }> => {
    setUploading(true);
    setUploadError(null);
    let newUploadedImageUrls: string[] = [];
    let uploadedVideoUrl: string | null = null;

    if (images.length > 0) {
      for (const file of images) {
        try {
          const downloadURL = await uploadProfileAssetToStorage(file, userId, 'listing_image', isAuthenticated);
          newUploadedImageUrls.push(downloadURL);
        } catch (uploadError) {
          setUploadError("Gabim gjatë ngarkimit të imazhit. Ju lutemi provoni përsëri.");
          setUploading(false);
          throw new Error("Failed to upload images");
        }
      }
    }

    if (videoFile) {
      setVideoUploading(true);
      try {
        uploadedVideoUrl = await uploadProfileAssetToStorage(videoFile, userId, 'listing_video', isAuthenticated);
      } catch (uploadError) {
        setUploadError("Gabim gjatë ngarkimit të videos. Ju lutemi provoni përsëri.");
        setUploading(false);
        setVideoUploading(false);
        throw new Error("Failed to upload video");
      }
      setVideoUploading(false);
    } else if (editMode && initialVideoUrl) {
        // Keep existing video URL if no new video is uploaded
    }

    setUploading(false);
    return { imageUrls: newUploadedImageUrls, videoUrl: uploadedVideoUrl };
  };
  
  const validateFormData = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.title || formData.title.trim().length < 5) {
      errors.title = "Titulli duhet të ketë të paktën 5 karaktere";
    }
    
    const price = parseFloat(formData.price);
    if (isNaN(price) || price <= 0) {
      errors.price = "Çmimi duhet të jetë një numër pozitiv";
    }
    
    const stock = parseInt(formData.stock, 10);
    if (isNaN(stock) || stock <= 0) {
      errors.stock = "Sasia duhet të jetë një numër i plotë pozitiv";
    }
    
    if (!formData.location || formData.location.trim() === '') {
      errors.location = "Lokacioni është i detyrueshëm";
    }
    
    if (!formData.category) {
      errors.category = "Kategoria është e detyrueshme";
    }
    
    if (!formData.phone || formData.phone.trim() === '') {
      errors.phone = "Numri i telefonit është i detyrueshëm";
    }
    
    if (!isAuthenticated) {
      if (!formData.email || formData.email.trim() === '') {
        errors.email = "Email-i është i detyrueshëm për përdoruesit pa llogari";
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          errors.email = "Ju lutemi shkruani një adresë email të vlefshme";
        }
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isAuthenticated && isLoadingCredits) {
      toast({
        title: "Duke kontrolluar kreditet...",
        description: "Ju lutemi prisni.",
        variant: "default",
      });
      return;
    }

    if (!validateForm() || !validateFormData()) {
      const firstError = Object.values(validationErrors)[0];
      if (firstError) {
        toast({
          title: "Gabim validimi",
          description: firstError,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Gabim validimi",
          description: "Ju lutemi kontrolloni formularin për gabime",
          variant: "destructive",
        });
      }
      return;
    }

    if (isAuthenticated && !editMode && hasNoCreditsLeft) {
      toast({
        title: "Nuk keni kredite",
        description: "Ju nuk keni më kredite në dispozicion. Ju lutemi blini kredite.",
        variant: "destructive",
      });
      navigate("/coins");
      return;
    }

    if (stockItemsOverLimit) {
      toast({
        title: "Limit i stokut i tejkaluar",
        description: `Plani juaj lejon maksimumi ${stockItemsLimit} artikuj në magazinë.`,
        variant: "destructive",
      });
      if (isAuthenticated) {
        navigate("/subscription");
      }
      return;
    }

    try {
      setIsSubmitting(true);

      const { imageUrls: uploadedImageUrls, videoUrl: uploadedVideoUrl } = await uploadAssets();
      
      const stockValue = parseInt(formData.stock, 10);
      const priceValue = parseFloat(formData.price);
      let shippingCostValue = null;
      if (formData.shippingCost && formData.shippingCost.trim() !== '') {
        shippingCostValue = parseFloat(formData.shippingCost);
        if (isNaN(shippingCostValue)) {
          shippingCostValue = null;
        }
      }
      
      let finalVideoUrl = uploadedVideoUrl;
      if (!finalVideoUrl && editMode) {
        finalVideoUrl = initialVideoUrl; 
      }
      
      const listingData: any = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: priceValue,
        currency: "EUR",
        category_id: formData.category ? parseInt(formData.category, 10) : null,
        subcategory_id: formData.subcategory ? parseInt(formData.subcategory, 10) : null,
        condition: formData.condition,
        country: formData.country,
        location: formData.location.trim(),
        images: uploadedImageUrls,
        video_url: finalVideoUrl,
        user_id: isAuthenticated ? userId : null,
        delivery_option: formData.deliveryOption,
        stock: stockValue,
        is_active: false,
        approval_status: "pending",
        shipping_cost: shippingCostValue,
        phone: formData.phone.trim(),
        phone_country_code: formData.phoneCountryCode,
        whatsapp_enabled: formData.whatsappEnabled,
        viber_enabled: formData.viberEnabled,
        is_price_negotiable: formData.isPriceNegotiable === 'true',
        contact_email: !isAuthenticated ? formData.email.trim() : undefined,
        is_authenticated_listing: isAuthenticated,
      };

      console.log("Submitting listing data:", JSON.stringify(listingData));
      
      let success;
      if (editMode && listingId) {
        console.log(`Calling updateListing for ID: ${listingId} with data:`, listingData);
         toast({ title: "Edit Mode", description: "Video URL would be saved here in edit mode.", variant: "default"});
         success = true;
      } else {
        success = await createListing(listingData);
      }
        
      if (!success) {
        throw new Error(editMode ? "Failed to update listing" : "Failed to create listing");
      }
      
      if (isAuthenticated && !editMode) {
        console.log("Deducting listing credit for user:", userId);
        const { error: creditError } = await supabase.rpc(
            "deduct_listing_credit", 
            { p_user_id: userId }
        );
          
        if (creditError) {
            console.error("Error calling deduct_listing_credit RPC:", creditError.message);
            toast({
              title: "Problem me Kreditin", // Changed title to "Credit Problem"
              description: "Njoftimi u krijua, por pati një problem me zbritjen e kreditit. Ju lutemi kontaktoni mbështetjen.", // Changed description
              variant: "destructive",
            });
        } else {
            console.log("Credit deduction RPC call initiated successfully for user:", userId);
        }
      }

      if (isAuthenticated) {
        try {
          const { data: userData, error: userError } = await supabase
            .from('profiles')
            .select('email, username')
            .eq('id', userId)
            .maybeSingle();
            
          if (!userError && userData?.email) {
            await sendListingCreationEmail(
              userData.email, 
              userData.username || 'User', 
              formData.title
            );
          }
        } catch (error) {
          toast({
            title: "Information",
            description: "Shpallja u krijua, por email-i i konfirmimit nuk u dërgua.",
            variant: "default",
          });
        }
      } else if (formData.email) {
        try {
          await sendListingCreationEmail(
            formData.email,
            "Guest User",
            formData.title
          );
        } catch (error) {
          console.error("Failed to send confirmation email to guest user", error);
        }
      }
      
      toast({
        title: "Sukses",
        description: `Shpallja u ${editMode ? 'përditësua' : 'krijua'} me sukses! ${uploadedImageUrls.length > 0 ? 'Imazhet' : ''} ${uploadedVideoUrl ? (uploadedImageUrls.length > 0 ? 'dhe videoja po përpunohen' : 'Videoja po përpunohet') : (uploadedImageUrls.length > 0 ? 'po përpunohen' : '')}...`,
      });

      setProcessingImages(true);
      setTimeout(() => {
        setProcessingImages(false);
        toast({
          title: "Procesimi përfundoi",
          description: `Assets për shpalljen tuaj janë gati.`,
          variant: "default",
        });
        navigate(editMode && listingId ? `/listing/${listingId}` : "/"); 
      }, 3000);
    } catch (error: any) {
      let errorMessage = error.message || "Gabim gjatë krijimit të shpalljes";
      if (uploadError) {
        errorMessage = uploadError;
      }
      console.error("Error submitting listing:", errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    uploading: uploading || videoUploading,
    uploadError,
    processingImages,
    handleSubmit,
    validationErrors,
    videoUploadProgress,
  };
};
